'use client';

import { c as _c } from "react/compiler-runtime";
import { motion } from 'framer-motion';
import { parseAsArrayOf, parseAsString, useQueryState } from 'nuqs';
import type { CommonCmsBlockProps } from '@/components/blocks/CmsBlock';
import { cn } from '@/lib/cn';
import { hasFleetCategoryImage } from '@/lib/fleetCategoryImage';
import type { CmsFleetCategory } from '@/services/cms-sdk/';
import { Filters } from './Filters';
import { FleetTypeCard } from './FleetTypeCard';
export interface FilteredFleetTypeCardsProps extends CommonCmsBlockProps {
  fleetCategories?: CmsFleetCategory[];
  showFilters?: boolean;
  vehicleCTALabel?: string;
}
const sortFilters = (a: number | string, b: number | string) => a < b ? -1 : a > b ? 1 : 0;
const sortAndMapItems = <T extends string | number,>(inSet: Set<T>, selectionArray: string[] | null) => [...inSet.values()].sort(sortFilters).map(s => ({
  isChecked: selectionArray?.includes(`${s}`) || false,
  label: `${s}`
}));
const createFCKey = (fc: CmsFleetCategory) => `${fc.id}-${fc.region}`;
export const filterAndSetItems = (prev: string[] | null, label: string | string[]): string[] | null => {
  const labels = typeof label === 'string' ? [label] : label;
  if (!prev) {
    return labels;
  }
  const result: string[] = [];
  for (const value of labels) {
    if (!prev.includes(value)) {
      result.push(value);
    }
  }
  for (const value of prev) {
    if (!labels.includes(value)) {
      result.push(value);
    }
  }
  return result.length > 0 ? result : null;
};
export function FilteredFleetTypeCards(t0) {
  const $ = _c(36);
  const {
    fleetCategories: allFleetCategories,
    preload,
    showFilters: t1,
    vehicleCTALabel
  } = t0;
  const showFilters = t1 === undefined ? true : t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = parseAsArrayOf(parseAsString);
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const [features, setFeatures] = useQueryState("features", t2);
  let t3;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = parseAsArrayOf(parseAsString);
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  const [seats, setSeats] = useQueryState("seats", t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = parseAsArrayOf(parseAsString);
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const [sleeps, setSleeps] = useQueryState("sleeps", t4);
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = parseAsArrayOf(parseAsString);
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  const [tags, setTags] = useQueryState("best-for", t5);
  if (!allFleetCategories) {
    return null;
  }
  let clearAll;
  let filteredCategories;
  let t6;
  let t7;
  let t8;
  if ($[4] !== allFleetCategories || $[5] !== features || $[6] !== preload || $[7] !== seats || $[8] !== setFeatures || $[9] !== setSeats || $[10] !== setSleeps || $[11] !== setTags || $[12] !== showFilters || $[13] !== sleeps || $[14] !== tags || $[15] !== vehicleCTALabel) {
    const fleetCategories = allFleetCategories.filter(hasFleetCategoryImage);
    let t9;
    if ($[21] !== setFeatures || $[22] !== setSeats || $[23] !== setSleeps || $[24] !== setTags) {
      t9 = async () => {
        await setFeatures(null);
        await setSeats(null);
        await setSleeps(null);
        await setTags(null);
      };
      $[21] = setFeatures;
      $[22] = setSeats;
      $[23] = setSleeps;
      $[24] = setTags;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    clearAll = t9;
    const prepareFilters = () => {
      if (!showFilters) {
        return [];
      }
      const seatsSet = new Set();
      const sleepsSet = new Set();
      const featuresSet = new Set();
      const tagsSet = new Set();
      for (const category of fleetCategories) {
        if (category.seatCount) {
          seatsSet.add(category.seatCount);
        }
        if (category.data.tag?.name) {
          tagsSet.add(category.data.tag.name);
        }
        if (category.sleepCount !== undefined) {
          sleepsSet.add(category.sleepCount);
        }
        const dataKeyFeatures = category.data?.keyFeatures || [];
        for (const feature of dataKeyFeatures) {
          featuresSet.add(feature.heading.trim().toLowerCase());
        }
      }
      const allFilters = [];
      if (sleepsSet.size) {
        allFilters.push({
          clear: () => setSleeps([]),
          items: sortAndMapItems(sleepsSet, sleeps),
          selectedCount: sleeps?.length || 0,
          title: "Sleeping capacity",
          toggle: label => setSleeps(prev => filterAndSetItems(prev, label))
        });
      } else {
        if (seatsSet.size) {
          allFilters.push({
            clear: () => setSeats([]),
            items: sortAndMapItems(seatsSet, seats),
            selectedCount: seats?.length || 0,
            title: "Seat capacity",
            toggle: label_0 => setSeats(prev_0 => filterAndSetItems(prev_0, label_0))
          });
        }
      }
      if (featuresSet.size) {
        allFilters.push({
          clear: () => setFeatures([]),
          items: sortAndMapItems(featuresSet, features),
          selectedCount: features?.length || 0,
          title: "Features",
          toggle: label_1 => setFeatures(prev_1 => filterAndSetItems(prev_1, label_1))
        });
      }
      if (tagsSet.size) {
        allFilters.push({
          clear: () => setTags([]),
          items: sortAndMapItems(tagsSet, tags),
          selectedCount: tags?.length || 0,
          title: "Best for",
          toggle: label_2 => setTags(prev_2 => filterAndSetItems(prev_2, label_2))
        });
      }
      return allFilters;
    };
    const filters = prepareFilters();
    filteredCategories = fleetCategories.filter(fleetCategory => {
      if (seats && seats.length && !seats.includes(`${fleetCategory.seatCount}`)) {
        return false;
      }
      if (sleeps && sleeps.length && (fleetCategory.sleepCount === undefined || !sleeps.includes(`${fleetCategory.sleepCount}`))) {
        return false;
      }
      const dataKeyFeatures_0 = fleetCategory.data?.keyFeatures || [];
      const keyFeatures = dataKeyFeatures_0.map(_temp);
      if (features && features.length && !features.every(feature_0 => keyFeatures.includes(feature_0))) {
        return false;
      }
      if (tags && tags.length && !tags.includes(`${fleetCategory.data.tag?.name}`)) {
        return false;
      }
      return true;
    }).map(createFCKey) || [];
    t8 = <Filters clearAll={clearAll} filters={filters} itemCount={filteredCategories.length} maxItems={fleetCategories.length} />;
    t6 = "flex flex-wrap gap-10 sm:gap-6 md:gap-12 flex-col pt-6 sm:flex-row";
    t7 = fleetCategories.map(fleetCategory_0 => {
      const key = createFCKey(fleetCategory_0);
      const visible = filteredCategories.includes(key);
      return <motion.div className={cn("sm:max-w-[347px] md:max-w-104 flex items-stretch", {
        "absolute h-0 border-0 overflow-hidden": !visible
      })} key={key} exit={{
        opacity: 0
      }} layout={visible}><FleetTypeCard fleetCategory={fleetCategory_0} preload={preload} vehicleCTALabel={vehicleCTALabel} /></motion.div>;
    });
    $[4] = allFleetCategories;
    $[5] = features;
    $[6] = preload;
    $[7] = seats;
    $[8] = setFeatures;
    $[9] = setSeats;
    $[10] = setSleeps;
    $[11] = setTags;
    $[12] = showFilters;
    $[13] = sleeps;
    $[14] = tags;
    $[15] = vehicleCTALabel;
    $[16] = clearAll;
    $[17] = filteredCategories;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
  } else {
    clearAll = $[16];
    filteredCategories = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
  }
  let t9;
  if ($[26] !== clearAll || $[27] !== filteredCategories.length) {
    t9 = filteredCategories.length === 0 ? <div className="flex flex-col gap-6 items-center w-full"><div>No vehicles match your current filters.</div><button className="btn bg-white text-primary pl-8 pr-7 border-primary h-13" onClick={clearAll}>Clear all filters</button></div> : null;
    $[26] = clearAll;
    $[27] = filteredCategories.length;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t6 || $[30] !== t7 || $[31] !== t9) {
    t10 = <div className={t6}>{t7}{t9}</div>;
    $[29] = t6;
    $[30] = t7;
    $[31] = t9;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  let t11;
  if ($[33] !== t10 || $[34] !== t8) {
    t11 = <>{t8}{t10}</>;
    $[33] = t10;
    $[34] = t8;
    $[35] = t11;
  } else {
    t11 = $[35];
  }
  return t11;
}
function _temp(t0) {
  const {
    heading
  } = t0;
  return heading.trim().toLowerCase();
}